import {isFeatureEnabled} from '@github-ui/feature-flags'
import {issueAgentTasksPath} from '@github-ui/paths'
import {useQuery, useQueryClient, type UseQueryResult} from '@github-ui/react-query'
import {useAlive} from '@github-ui/use-alive'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import throttle from 'lodash-es/throttle'
import {createContext, use, useEffect, useMemo, type ReactNode} from 'react'

import type {APITask} from '@github-ui/agent-sessions/types/api-task'
import type {AgentInfo} from '@github-ui/agent-sessions/types/session'
import {useIsVisualizationEnabled} from '../utils/is-visualization-enabled'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type IssueAgentTask = APITask & {
  url?: string
}

export type IssueAssignedAgentInfo = AgentInfo & {
  // TODO: The API which serves `AgentInfo` also returns a number, but the type appears to be incorrect. I didn't
  // want to add that change to the scope of this work though.
  id: number
}

export type IssueAgentTasksResponse = {
  tasks: IssueAgentTask[]
  assigned_agents?: IssueAssignedAgentInfo[]
  issue_update_channel?: string
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const IssueAgentTasksContext = createContext<UseQueryResult<IssueAgentTasksResponse> | null>(null)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

type IssueAgentTasksProviderProps = {
  owner: string
  repo: string
  issueNumber: number
  children: ReactNode
}

const THROTTLE_INTERVAL_MS = 2000

const getQueryKey = (owner: string, repo: string, issueNumber: number) =>
  ['issue-agent-tasks', owner, repo, issueNumber] as const

/**
 * Provides agent-task data for a given issue to all descendants.
 *
 * Fetches tasks via the REST API and subscribes to real-time updates through
 * the alive channel returned in the API response (`issue_update_channel`).
 */
export function IssueAgentTasksProvider({owner, repo, issueNumber, children}: IssueAgentTasksProviderProps) {
  const queryClient = useQueryClient()

  const visualizationEnabled = useIsVisualizationEnabled()

  const isAgentTasksFetchEnabled = visualizationEnabled || isFeatureEnabled('issue_cca_visualization_session_panel')
  const enabled =
    isAgentTasksFetchEnabled && owner.length > 0 && repo.length > 0 && Number.isInteger(issueNumber) && issueNumber > 0

  const queryResult = useQuery<IssueAgentTasksResponse>({
    enabled,
    queryKey: getQueryKey(owner, repo, issueNumber),
    queryFn: async () => {
      const response = await reactFetchJSON(issueAgentTasksPath({owner, repo, issueNumber}))

      if (!response.ok) {
        throw new Error(`Failed to fetch agent tasks: ${response.status}`)
      }

      return (await response.json()) as IssueAgentTasksResponse
    },
    retry: false,
  })

  const throttledRefetch = useMemo(
    () =>
      throttle(
        () =>
          queryClient.invalidateQueries({
            queryKey: getQueryKey(owner, repo, issueNumber),
            exact: true,
            refetchType: 'active',
          }),
        THROTTLE_INTERVAL_MS,
        {leading: false, trailing: true},
      ),
    [queryClient, owner, repo, issueNumber],
  )

  useEffect(() => {
    return () => throttledRefetch.cancel()
  }, [throttledRefetch])

  const aliveChannel = queryResult.data?.issue_update_channel
  useAlive(enabled ? aliveChannel : undefined, throttledRefetch)

  return <IssueAgentTasksContext value={queryResult}>{children}</IssueAgentTasksContext>
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the agent-task query result from the nearest `IssueAgentTasksProvider`.
 *
 * Must be called within an `IssueAgentTasksProvider`.
 */
export function useIssueAgentTasks(): UseQueryResult<IssueAgentTasksResponse> {
  const ctx = use(IssueAgentTasksContext)
  if (ctx === null) {
    throw new Error('useIssueAgentTasks must be used within an IssueAgentTasksProvider')
  }
  return ctx
}
